import i18n from "i18next";

type Locale = "ru" | "en" | "de" | "fr" | "es";

/** Совпадает с `fallbackLng` в `src/i18n/config.ts`. */
const fallbackLocale: Locale = "en";
type FormatType = "short" | "long";

const dateTimeFormats: Record<
  Locale,
  Record<FormatType, Intl.DateTimeFormatOptions>
> = {
  ru: {
    short: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
  },
  en: {
    short: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
  },
  de: {
    short: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
  },
  fr: {
    short: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
  },
  es: {
    short: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    },
  },
};

const numberFormats: Record<
  Locale,
  Record<string, Intl.NumberFormatOptions>
> = {
  ru: {
    currency: {
      style: "currency",
      currency: "RUB",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  en: {
    currency: {
      style: "currency",
      currency: "USD",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  de: {
    currency: {
      style: "currency",
      currency: "EUR",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  fr: {
    currency: {
      style: "currency",
      currency: "EUR",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  es: {
    currency: {
      style: "currency",
      currency: "EUR",
    },
    decimal: {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
};

/**
 * Локаль, для которой в картах ниже ГАРАНТИРОВАННО есть запись.
 *
 * `i18n.language as Locale` было ложью компилятору: значение не обязано
 * лежать в карте пяти. Два реальных входа мимо неё —
 *  1) до завершения `init()` (детектор + загрузка словарей по HTTP)
 *     свойство ещё `undefined`;
 *  2) i18next сам дописывает `cimode` в `supportedLngs` (`transformOptions`),
 *     и в этом режиме язык — `cimode`.
 * В обоих случаях `formats[locale]` = `undefined`, и следующий индекс
 * `[format]` бросал TypeError вместо того, чтобы показать значение.
 */
const resolveLocale = (): Locale => {
  const lng = i18n.language;
  return lng && lng in dateTimeFormats ? (lng as Locale) : fallbackLocale;
};

export const formatDateTime = (
  date: Date,
  format: FormatType = "short"
): string => {
  const currentLocale = resolveLocale();
  return new Intl.DateTimeFormat(
    currentLocale,
    dateTimeFormats[currentLocale][format]
  ).format(date);
};

// Alias for backward compatibility with string support
export const formatDate = (
  date: Date | string,
  format: FormatType = "short"
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return formatDateTime(dateObj, format);
};

export const formatNumber = (
  number: number,
  format: string = "decimal"
): string => {
  const currentLocale = resolveLocale();
  return new Intl.NumberFormat(
    currentLocale,
    numberFormats[currentLocale][format]
  ).format(number);
};

export const formatRelativeTime = (date: Date | string): string => {
  const currentLocale = i18n.language;
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(currentLocale, { numeric: "auto" });

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, "second");
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return rtf.format(-diffInMinutes, "minute");
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return rtf.format(-diffInHours, "hour");
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return rtf.format(-diffInDays, "day");
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return rtf.format(-diffInMonths, "month");
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return rtf.format(-diffInYears, "year");
};

export type Currency = "RUB" | "USD" | "EUR" | "GBP" | "CNY" | "JPY" | "CHF" | "CAD" | "AUD" | "KRW" | "KZT" | "BYN";

interface CurrencyConfig {
  code: Currency;
  symbol: string;
  position: "before" | "after";
  decimalSeparator: string;
  thousandSeparator: string;
}

const currencyConfigs: Record<Currency, CurrencyConfig> = {
  RUB: {
    code: "RUB",
    symbol: "₽",
    position: "after",
    decimalSeparator: ",",
    thousandSeparator: " ",
  },
  USD: {
    code: "USD",
    symbol: "$",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    position: "after",
    decimalSeparator: ",",
    thousandSeparator: ".",
  },
  GBP: {
    code: "GBP",
    symbol: "£",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  CNY: {
    code: "CNY",
    symbol: "¥",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  JPY: {
    code: "JPY",
    symbol: "¥",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  CHF: {
    code: "CHF",
    symbol: "CHF",
    position: "after",
    decimalSeparator: ".",
    thousandSeparator: "'",
  },
  CAD: {
    code: "CAD",
    symbol: "C$",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  AUD: {
    code: "AUD",
    symbol: "A$",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  KRW: {
    code: "KRW",
    symbol: "₩",
    position: "before",
    decimalSeparator: ".",
    thousandSeparator: ",",
  },
  KZT: {
    code: "KZT",
    symbol: "₸",
    position: "after",
    decimalSeparator: ",",
    thousandSeparator: " ",
  },
  BYN: {
    code: "BYN",
    symbol: "Br",
    position: "after",
    decimalSeparator: ",",
    thousandSeparator: " ",
  },
};

const defaultCurrencies: Record<string, Currency> = {
  ru: "RUB",
  en: "USD",
  de: "EUR",
  fr: "EUR",
  es: "EUR",
};

export const formatCurrency = (
  amount: number,
  currency?: Currency,
  locale?: string
): string => {
  const currentLocale = locale || i18n.language;
  // Локаль не в карте (напр. 'it', 'pl') — валюту показа взять неоткуда.
  // Падаем в базовую валюту платформы (EUR, ADR-MONEY §7), не в доллар.
  const fallbackCurrency: Currency = 'EUR';
  const currentCurrency =
    currency || defaultCurrencies[currentLocale] || fallbackCurrency;
  const config = currencyConfigs[currentCurrency];

  const formatter = new Intl.NumberFormat(currentLocale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formattedNumber = formatter
    .format(amount)
    .replace(".", config.decimalSeparator)
    .replace(",", config.thousandSeparator);

  return config.position === "before"
    ? `${config.symbol}${formattedNumber}`
    : `${formattedNumber}${config.symbol}`;
};
